import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { MAX_DISPLAY_NAME_LENGTH } from '@shared/index'
import { useQueryClient } from '@tanstack/react-query'
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut, updateUserAttributes } from 'aws-amplify/auth'

import { log } from '@/lib/utils/log'

import { configureAuth, isAuthConfigured } from './config'

export type AuthUser = {
  userId: string
  email: string
  displayName: string
  groups: string[]
  isAdmin: boolean
}

type AuthContextValue = {
  user: AuthUser | null
  status: 'loading' | 'ready'
  isConfigured: boolean
  refresh: () => Promise<AuthUser | null>
  updateDisplayName: (name: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

function claimGroups(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string')
    return value
      .split(',')
      .map((group) => group.trim())
      .filter(Boolean)
  return []
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready'>('loading')
  const currentUserRef = useRef<AuthUser | null>(null)
  const sessionVersion = useRef(0)
  const queryClient = useQueryClient()
  const cachedAccount = useRef<string | null>(null)

  // Cached responses (Me history filters, settings, admin data) belong to one account. Drop them whenever the
  // signed-in account changes, including an expired session followed by a different sign-in.
  useEffect(() => {
    const account = user?.userId ?? null
    if (cachedAccount.current && cachedAccount.current !== account) queryClient.clear()
    cachedAccount.current = account
  }, [user?.userId, queryClient])

  const refresh = useCallback(async () => {
    const version = ++sessionVersion.current
    if (!configureAuth()) {
      currentUserRef.current = null
      setUser(null)
      setStatus('ready')
      return null
    }

    try {
      const [currentUser, session] = await Promise.all([getCurrentUser(), fetchAuthSession()])
      const claims = session.tokens?.idToken?.payload
      if (!claims) throw new Error('No ID token is available')

      // Attributes are current even when the cached ID token predates a profile edit.
      const attributes = await fetchUserAttributes().catch(() => undefined)
      if (version !== sessionVersion.current) return null

      const groups = claimGroups(claims['cognito:groups'])
      const displayName = attributes ? (attributes.name ?? '') : typeof claims.name === 'string' ? claims.name : ''
      const nextUser: AuthUser = {
        userId: currentUser.userId,
        email: typeof claims.email === 'string' ? claims.email : currentUser.username,
        displayName: displayName.trim(),
        groups,
        isAdmin: groups.includes('admin')
      }
      currentUserRef.current = nextUser
      setUser(nextUser)
      log('Cognito session restored', 'user', {
        email: nextUser.email,
        groups: nextUser.groups,
        role: nextUser.isAdmin ? 'Administrator' : 'User'
      })
      return nextUser
    } catch {
      if (version === sessionVersion.current) {
        currentUserRef.current = null
        setUser(null)
      }
      return null
    } finally {
      if (version === sessionVersion.current) setStatus('ready')
    }
  }, [])

  const updateDisplayName = useCallback(async (name: string) => {
    const displayName = name.trim()
    if (!displayName || displayName.length > MAX_DISPLAY_NAME_LENGTH) {
      throw new Error(`Enter a nickname between 1 and ${MAX_DISPLAY_NAME_LENGTH} characters.`)
    }

    const account = currentUserRef.current
    if (!account) throw new Error('Sign in to update your nickname.')
    const version = sessionVersion.current

    await updateUserAttributes({ userAttributes: { name: displayName } })
    if (version !== sessionVersion.current || currentUserRef.current?.userId !== account.userId) return

    const nextUser = { ...currentUserRef.current, displayName }
    currentUserRef.current = nextUser
    setUser(nextUser)

    // Persistence has succeeded. A token-refresh failure must not undo the saved name or sign the user out.
    try {
      await fetchAuthSession({ forceRefresh: true })
    } catch {
      log('Nickname saved; session token refresh unavailable', 'user')
    }
  }, [])

  const logout = useCallback(async () => {
    const version = ++sessionVersion.current
    if (isAuthConfigured) await signOut()
    if (version === sessionVersion.current) {
      currentUserRef.current = null
      setUser(null)
      setStatus('ready')
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, isConfigured: isAuthConfigured, refresh, updateDisplayName, logout }),
    [logout, refresh, status, updateDisplayName, user]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

/** True for signed-in admins. Safe outside AuthProvider (e.g. Storybook), where it is false. */
export function useIsAdmin(): boolean {
  return useContext(AuthContext)?.user?.isAdmin === true
}

/** The current account, also safe for isolated history stories and tests. */
export function useAuthUser(): AuthUser | null {
  return useContext(AuthContext)?.user ?? null
}
